import { useEffect, useRef } from 'react';

/**
 * Calls `callbackFunction` once the element has been at least `threshold`
 * visible on screen for `delay` ms without being scrolled away. Works inside
 * an iframe: the browser measures against the real window, not the iframe.
 */
const useVisibilityTracking = <T extends HTMLElement = HTMLElement>(
    callbackFunction: () => void,
    threshold: number = 1.0,
    delay: number = 0,
) => {
    const elementRef = useRef<T>(null);

    // In a ref so an inline callback does not rebuild the observer every render.
    const callbackRef = useRef(callbackFunction);
    callbackRef.current = callbackFunction;

    useEffect(() => {
        const element = elementRef.current;
        if (!element) {
            return;
        }

        let countdown: ReturnType<typeof setTimeout> | undefined;

        const stopCountdown = () => {
            clearTimeout(countdown);
            countdown = undefined;
        };

        const observer = new IntersectionObserver(
            ([entry]) => {
                // Not `entry.isIntersecting`: that is already true at one pixel.
                const isVisibleEnough = entry.intersectionRatio >= threshold;

                if (!isVisibleEnough) {
                    stopCountdown();
                    return;
                }

                // Already counting down, let it finish.
                if (countdown !== undefined) {
                    return;
                }

                countdown = setTimeout(() => callbackRef.current(), delay);
            },
            { threshold },
        );

        observer.observe(element);

        return () => {
            observer.disconnect();
            stopCountdown();
        };
    }, [threshold, delay]);

    return elementRef;
};

export default useVisibilityTracking;
